import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import objectDiff from 'modules/taboola-common-frontend-modules/utils/objectDiff';
import { accountsApi, useAccountsApi } from 'services/api';
import { errorMessagesUtils } from '../../../services/utils';
import { addIndication, INDICATION_TYPES } from '../../taboola-common-frontend-modules/Indications';
import { FormattedMessage } from '../../taboola-common-frontend-modules/i18n';
import { useMultiLayerPathParams } from '../../taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerPathParams';
import { updateAccountSuccess } from '../actions/updateAccountSuccess';
import { ACCOUNTS_ROUTE } from '../config';

export const generateFetchErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="account.fetch.error.highlight" defaultMessage="Unable to fetch account." />,
});

export const successIndication = {
    message: <FormattedMessage id="account.save.success" defaultMessage="Account successfully updated" />,
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
};

export const generateErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="account.save.error.highlight" defaultMessage="Unable to save changes." />,
});

export const useAccountEditorState = () => {
    const dispatch = useDispatch();
    const { getAdvertiser } = useAccountsApi();
    const { fetchedData: originalAccount } = useFormDataContext();
    const { accountId: accountName } = useMultiLayerPathParams();
    const formDataFetcher = useCallback(async () => {
        try {
            const data = await getAdvertiser(accountName);
            return data;
        } catch (error) {
            dispatch(addIndication(generateFetchErrorIndication(error)));
            throw error;
        }
    }, [dispatch, accountName, getAdvertiser]);

    const { submit } = useFormState({ formDataFetcher });

    const handleEditAccountSubmitWrapper = useCallback(() => {
        const updateAccountWrapper = async account => {
            const accountChanges = objectDiff(account, originalAccount);
            const accountChangesWithName = {
                ...accountChanges,
                adminNetworkAccountId: account.adminNetworkAccountId,
            };

            try {
                const updateAccountResponse = await accountsApi.updateAdvertiser(accountName, accountChangesWithName);
                dispatch(addIndication(successIndication));
                dispatch(updateAccountSuccess({ ...updateAccountResponse, id: updateAccountResponse.accountId }));
                return updateAccountResponse;
            } catch (error) {
                dispatch(addIndication(generateErrorIndication(error)));
                throw error;
            }
        };
        return submit({
            saveFormData: updateAccountWrapper,
            successUrl: ACCOUNTS_ROUTE,
        });
    }, [dispatch, submit, originalAccount, accountName]);

    return { submit: handleEditAccountSubmitWrapper };
};
